import {
  Box,
  Card,
  CircularProgress,
  Container,
  IconButton,
  Typography,
  Chip,
  Stack,
  CardContent,
  CardHeader,
  Divider,
  Paper,
  Badge,
  Fade,
  Tooltip,
  Avatar,
  Checkbox,
  Button,
  useTheme,
} from "@mui/material";
import React, { useEffect, useState, useCallback, useMemo } from "react";
import PersonIcon from "@mui/icons-material/Person";
import { useQuery, useMutation } from "@apollo/client";
import ReusableTable from "../../components/ReusableTable/ReusableTable";
import ReadinessChip from "../../components/ReadinessChip/ReadinessChip";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import FilterListIcon from "@mui/icons-material/FilterList";
import ClearIcon from "@mui/icons-material/Clear";
import AssessmentIcon from "@mui/icons-material/Assessment";
import ControlDetailDialog from "../Settings/Frameworks/FrameWorkDialogs/ControlDetailsDialog";
import { GET_FRAMEWORK_DETAIL_BY_ID } from "../../apis/dashboard-apis/dashboardDetailApi";
import { UPDATE_CONTROL_OWNER_BULK } from "../../apis/settingsApis/framework-apis/frameworkApis";
import useAppStore from "../../stores";
import { CheckStatus } from "../../utils/enums";
import SearchBar from "../../components/SearchBar/SearchBar";
import InformationStepsCard from "../../components/InformationStepsCard/InformationStepsCard";
import UserSelectionDialog from "../../components/UserSelectionDialog";
import ControlOwnerAvatar from "../../components/ControlOwnerAvatar/ControlOwnerAvatar";
import AIEvidenceChip from "../../components/AIEvidenceChip/AIEvidenceChip";
import AIEvidenceAccessEmptyState from "./AIEvidenceAccessEmptyState";
import { GET_AI_EVIDENCE_SUMMARY } from "../../apis/integrated-evidence/aiEvidenceApis";
import ColumnFilter from "../../components/ReusableTable/FilterComponents/ColumnFilter";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import {
  FEATURE_IDS,
  useFeatureEnabled,
} from "../../components/AccessControl";

const columns = [
  { label: "", field: "select", align: "left", width: "5%" },
  { label: "Control Name", field: "controlsName", align: "left", width: "8%" },
  { label: "Control", field: "control_info", align: "left", width: "38%" },
  { label: "Owner", field: "owner", align: "left", width: "11%" },
  { label: "Domain", field: "domain", align: "left", width: "10%" },
  { label: "Rego AI", field: "aiEvidence", align: "left", width: "14%" },
  {
    label: "Readiness",
    field: "readiness",
    align: "left",
    width: "9%",
    sortConfig: { sortable: true },
  },
  { label: "", field: "actions", align: "left", width: "5%" },
];

const styles = {
  pageContainer: {
    padding: { xs: 2, sm: 3 },
    minHeight: "100vh",
  },
  statsCard: {
    p: 2,
    borderRadius: "12px",
    border: "1px solid #E8EAED",
    backgroundColor: "#FFFFFF",
    flex: 1,
    transition: "all 0.3s ease",
    "&:hover": {
      boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
      transform: "translateY(-2px)",
    },
  },
  mainCard: {
    minHeight: "600px",
    borderRadius: "12px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
    border: "1px solid #E8EAED",
    overflow: "hidden",
  },
  cardHeader: {
    borderBottom: "1px solid #ddd",
    background: "#F7F7FC",
    ".MuiCardHeader-title": {
      fontSize: "18px",
      fontWeight: "bold",
    },
    padding: "14px",
  },
  domainFilterContainer: {
    p: 3,
    pb: 2,
    backgroundColor: "#FEFEFE",
  },
  domainChip: {
    cursor: "pointer",
    height: "38px",
    fontWeight: 600,
    fontSize: "0.875rem",
    borderRadius: "12px",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    transform: "scale(1)",
    backgroundColor: "#ffffff",
    color: "#6B7280",
    border: "2px solid #E5E7EB",
    "&:hover": {
      backgroundColor: "#F9FAFB",
      borderColor: "#9CA3AF",
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
      transform: "scale(1.02)",
    },
  },
  domainChipSelected: {
    backgroundColor: "#3B82F6",
    color: "#ffffff",
    border: "2px solid #3B82F6",
    boxShadow: "0 4px 12px rgba(59, 130, 246, 0.3)",
    transform: "scale(1.05)",
    "&:hover": {
      backgroundColor: "#2563EB",
      boxShadow: "0 6px 16px rgba(59, 130, 246, 0.4)",
    },
  },
  badge: {
    "& .MuiBadge-badge": {
      fontSize: "0.7rem",
      minWidth: 18,
      height: 18,
      backgroundColor: "#3B82F6",
      color: "#ffffff",
    },
  },
  badgeSelected: {
    "& .MuiBadge-badge": {
      backgroundColor: "#ffffff",
      color: "#3B82F6",
    },
  },
  clearFilterButton: {
    color: "#6B7280",
    backgroundColor: "#F3F4F6",
    "&:hover": {
      backgroundColor: "#E5E7EB",
      color: "#374151",
    },
  },
  loadingContainer: {
    textAlign: "center",
    py: 12,
  },
  descriptionCell: {
    maxWidth: "300px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  actionButton: {
    color: "#031B89",
    "&:hover": {
      backgroundColor: "rgba(3, 27, 137, 0.08)",
      transform: "translateX(2px)",
      transition: "all 0.2s ease",
    },
  },
};

const ControlsHome = () => {
  const theme = useTheme();
  const [allControls, setAllControls] = useState([]);
  const [filteredControls, setFilteredControls] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [domains, setDomains] = useState([]);
  const [selectedControl, setSelectedControl] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedControlId, setSelectedControlId] = useState(null);
  const [sortBy, setSortBy] = useState([]); // e.g. ['-readiness'] or ['readiness'] or []
  const [controlsHydrated, setControlsHydrated] = useState(false);

  // Row selection & bulk owner change
  const [selectedRowIds, setSelectedRowIds] = useState(new Set());
  const [tableFilters, setTableFilters] = useState(() => {
    const param = new URLSearchParams(window.location.search).get("aiEvidence");
    return param ? { aiEvidence: param.split(",") } : {};
  });
  const [ownerDialog, setOwnerDialog] = useState({
    open: false,
    controlsIds: [],
    currentOwner: null,
  });

  // Get selected framework from store
  const selectedFrameworkId = useAppStore((state) => state.selectedFrameworkId);
  const selectedFramework = useAppStore((state) => state.selectedFramework);
  const aiEvidenceEnabled = useFeatureEnabled(
    FEATURE_IDS.REGO_AI_EVIDENCE_COLLECTION
  );

  // Fetch framework data using selected framework
  const {
    data: frameworkData,
    loading,
    refetch: refetchFrameworkDetails,
  } = useQuery(GET_FRAMEWORK_DETAIL_BY_ID, {
    variables: { framework_id: selectedFrameworkId },
    skip: !selectedFrameworkId, // Skip query if no framework is selected
  });

  const { data: aiSummaryData } = useQuery(GET_AI_EVIDENCE_SUMMARY, {
    variables: { frameworkId: selectedFrameworkId },
    skip: !aiEvidenceEnabled || !selectedFrameworkId,
    fetchPolicy: "cache-first",
  });

  const tableColumns = useMemo(
    () =>
      aiEvidenceEnabled
        ? columns
        : columns.filter((column) => column.field !== "aiEvidence"),
    [aiEvidenceEnabled]
  );

  const aiEvidenceByControl = useMemo(() => {
    const map = new Map();
    (aiSummaryData?.getAIEvidenceSummary?.byControl || []).forEach((entry) => {
      map.set(entry.controlId, entry);
    });
    return map;
  }, [aiSummaryData]);

  const ineligibleControlIds = useMemo(
    () =>
      new Set(aiSummaryData?.getAIEvidenceSummary?.ineligibleControlIds || []),
    [aiSummaryData]
  );

  const aiEvidenceFilterOptions = useMemo(
    () => [
      { id: "mine", label: "Awaiting my review" },
      { id: "pending", label: "Awaiting review" },
      { id: "approved", label: "Approved" },
      { id: "none", label: "No suggestions yet" },
      { id: "ineligible", label: "Not applicable" },
    ],
    []
  );

  useEffect(() => {
    setControlsHydrated(false);
  }, [selectedFrameworkId]);

  useEffect(() => {
    if (!frameworkData?.getFrameworkDetailByFrameworkId) return;

    const controlsMap = new Map();
    const domainsSet = new Set();

    // Process framework data to extract all controls
    frameworkData.getFrameworkDetailByFrameworkId.areaDetails?.forEach(
      (area) => {
        // Extract domain from area name - break at first "-" or ":" and take the first part
        let domainValue = area.area_name;
        if (area.area_name.includes("-")) {
          domainValue = area.area_name.split("-")[0].trim();
        }

        if (domainValue) {
          domainsSet.add(domainValue);
        }

        area?.criterias?.forEach((criteria) => {
          criteria?.controls?.forEach((control) => {
            if (control?.checks?.length > 0) {
              // Check if control already exists
              if (!controlsMap.has(control.controlsId)) {
                // First time seeing this control - create new entry
                const controlChecks = control?.checks || [];
                const totalChecks = controlChecks.length;
                const passingChecks = controlChecks.filter((check) =>
                  [CheckStatus.DISABLED, CheckStatus.PASS].includes(
                    check?.checkStatus
                  )
                ).length;

                const readinessPercentage =
                  totalChecks > 0 ? (passingChecks / totalChecks) * 100 : 0;

                controlsMap.set(control.controlsId, {
                  ...control,
                  domains: [area.area_name],
                  domainNumbers: [domainValue],
                  areaNames: [area.area_name],
                  readinessPercentage: parseInt(readinessPercentage),
                });
              } else {
                // Control already exists - append new domain info if not already present
                const existingControl = controlsMap.get(control.controlsId);
                if (!existingControl.domains.includes(area.area_name)) {
                  existingControl.domains.push(area.area_name);
                  existingControl.areaNames.push(area.area_name);
                }
                if (!existingControl.domainNumbers.includes(domainValue)) {
                  existingControl.domainNumbers.push(domainValue);
                }
              }
            }
          });
        });
      }
    );

    // Convert map to array and sort by control ID
    const controlsArray = Array.from(controlsMap.values()).sort(
      (a, b) => a.controlsId - b.controlsId
    );

    setAllControls(controlsArray);
    setFilteredControls(controlsArray);
    setDomains(
      Array.from(domainsSet).sort((a, b) => {
        const numA = parseInt(a.replace("CC", ""));
        const numB = parseInt(b.replace("CC", ""));
        return numA - numB;
      })
    );
    setControlsHydrated(true);
  }, [frameworkData]);

  // Update search filter effect to include domain filtering
  useEffect(() => {
    if (!searchQuery.trim() && !selectedDomain) {
      setFilteredControls(allControls);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = allControls.filter((control) => {
      // Domain filter - check if control belongs to selected domain
      if (selectedDomain && !control.domainNumbers?.includes(selectedDomain)) {
        return false;
      }

      // Search in control name and description
      const controlMatch =
        (control.controlsName?.toLowerCase() || "").includes(query) ||
        (control.controlsDescription?.toLowerCase() || "").includes(query);

      // Search in checks
      const checkMatch = control.checks?.some(
        (check) =>
          (check.activeCheckName?.toLowerCase() || "").includes(query) ||
          (check.activeCheckDescription?.toLowerCase() || "").includes(query)
      );

      return controlMatch || checkMatch;
    });

    setFilteredControls(filtered);
  }, [searchQuery, selectedDomain, allControls]);

  useEffect(() => {
    if (selectedControlId && allControls.length > 0) {
      const updated = allControls.find(
        (c) => c.controlsId === selectedControlId
      );
      setSelectedControl(updated || null);
    }
  }, [selectedControlId, allControls]);

  const handleOpenDialog = (control) => {
    setSelectedControlId(control.controlsId);
    setSelectedControl(control);
    setIsDialogOpen(true);
  };

  const clearFilters = () => {
    setSearchQuery("");
    setSelectedDomain(null);
  };

  const getDomainControlCount = (domain) => {
    return allControls.filter((control) =>
      control.domainNumbers?.includes(domain)
    ).length;
  };

  const getReadinessStats = () => {
    const total = filteredControls.length;
    const fullyReady = filteredControls.filter(
      (control) => control.readinessPercentage === 100
    ).length;
    const notReady = filteredControls.filter(
      (control) => control.readinessPercentage !== 100
    ).length;

    return { total, fullyReady, notReady };
  };

  const stats = getReadinessStats();

  const handleSortChange = useCallback((newSortBy) => {
    setSortBy(newSortBy);
  }, []);

  const ownerFilterOptions = useMemo(() => {
    const seen = new Set();
    const options = [];
    allControls.forEach((c) => {
      const name = c.controlOwnerData?.name;
      if (name && !seen.has(name)) {
        seen.add(name);
        options.push({ id: name, label: name });
      }
    });
    options.sort((a, b) => a.label.localeCompare(b.label));
    const hasUnassigned = allControls.some(
      (c) => !c.controlOwnerData?.name && !c.controlOwnerData?.email
    );
    if (hasUnassigned) options.unshift({ id: "__unassigned__", label: "Unassigned" });
    return options;
  }, [allControls]);

  const handleFilterChange = useCallback((field, values) => {
    setTableFilters((prev) => {
      const next = { ...prev };
      if (!values || values.length === 0) delete next[field];
      else next[field] = values;
      return next;
    });
  }, []);

  const ownerFilteredControls = useMemo(() => {
    const ownerFilters = tableFilters["owner"];
    const aiFilters = tableFilters["aiEvidence"];

    let rows = filteredControls;

    if (ownerFilters && ownerFilters.length > 0) {
      rows = rows.filter((c) => {
        const name = c.controlOwnerData?.name || "";
        const email = c.controlOwnerData?.email || "";
        const isUnassigned = !name && !email;
        if (ownerFilters.includes("__unassigned__") && isUnassigned) return true;
        return ownerFilters.includes(name);
      });
    }

    if (aiEvidenceEnabled && aiFilters && aiFilters.length > 0) {
      rows = rows.filter((c) => {
        const counts = aiEvidenceByControl.get(c.controlsId);
        const pending = counts?.pendingCount || 0;
        const approved = counts?.approvedCount || 0;
        const mine = counts?.myPendingCount || 0;
        const isIneligible = ineligibleControlIds.has(c.controlsId);

        return aiFilters.some((f) => {
          if (f === "mine") return mine > 0;
          if (f === "pending") return pending > 0;
          if (f === "approved") return approved > 0 && pending === 0;
          if (f === "none")
            return !isIneligible && pending === 0 && approved === 0;
          if (f === "ineligible")
            return isIneligible && pending === 0 && approved === 0;
          return false;
        });
      });
    }

    return rows;
  }, [
    filteredControls,
    tableFilters,
    aiEvidenceByControl,
    ineligibleControlIds,
    aiEvidenceEnabled,
  ]);

  // Build sorted list first so selection helpers can reference it
  const sortedControls = useMemo(() => {
    const first = sortBy[0];
    if (!first) return ownerFilteredControls;
    const field = first.startsWith("-") ? first.slice(1) : first;
    const direction = first.startsWith("-") ? "desc" : "asc";
    const accessor =
      field === "readiness"
        ? (row) => row.readinessPercentage ?? 0
        : (row) => row[field];
    const copy = [...ownerFilteredControls];
    copy.sort((a, b) => {
      const aVal = accessor(a);
      const bVal = accessor(b);
      const cmp =
        typeof aVal === "number" && typeof bVal === "number"
          ? aVal - bVal
          : String(aVal).localeCompare(String(bVal));
      return direction === "asc" ? cmp : -cmp;
    });
    return copy;
  }, [ownerFilteredControls, sortBy]);

  const showNoOwnedEvidence =
    aiEvidenceEnabled &&
    !loading &&
    sortedControls.length === 0 &&
    (tableFilters["aiEvidence"] || []).includes("mine");

  const shouldShowTableLoading =
    (loading || !controlsHydrated) && sortedControls.length === 0;

  const handleSelectRow = useCallback((controlsId) => {
    setSelectedRowIds((prev) => {
      const next = new Set(prev);
      next.has(controlsId) ? next.delete(controlsId) : next.add(controlsId);
      return next;
    });
  }, []);

  const handleSelectAll = useCallback(
    (e) => {
      if (e.target.checked) {
        setSelectedRowIds(new Set(sortedControls.map((c) => c.controlsId)));
      } else {
        setSelectedRowIds(new Set());
      }
    },
    [sortedControls]
  );

  const openOwnerDialog = useCallback((controlsIds, currentOwner = null) => {
    setOwnerDialog({ open: true, controlsIds, currentOwner });
  }, []);

  const closeOwnerDialog = useCallback(() => {
    setOwnerDialog({ open: false, controlsIds: [], currentOwner: null });
  }, []);

  const [updateOwner, { loading: ownerSaving }] = useMutation(UPDATE_CONTROL_OWNER_BULK);

  const handleOwnerConfirm = useCallback(
    async (user) => {
      await updateOwner({
        variables: {
          controls_ids: ownerDialog.controlsIds,
          control_owner_id: user?.id ?? null,
        },
      });
      setSelectedRowIds(new Set());
      closeOwnerDialog();
      refetchFrameworkDetails();
    },
    [updateOwner, ownerDialog.controlsIds, closeOwnerDialog, refetchFrameworkDetails]
  );

  const handleOwnerChangeSuccess = useCallback(() => {
    setSelectedRowIds(new Set());
    refetchFrameworkDetails();
  }, [refetchFrameworkDetails]);

  const getNextSortBy = useCallback(
    (field) => {
      const entry = sortBy.find((s) => s === field || s === `-${field}`);
      if (entry === field) return [`-${field}`];
      if (entry === `-${field}`) return [];
      return [field];
    },
    [sortBy]
  );

  const renderHeaderCell = useCallback(
    (column) => {
      if (column.field === "select") {
        const allSelected =
          sortedControls.length > 0 &&
          selectedRowIds.size === sortedControls.length;
        const someSelected =
          selectedRowIds.size > 0 &&
          selectedRowIds.size < sortedControls.length;
        return (
          <Checkbox
            size="small"
            checked={allSelected}
            indeterminate={someSelected}
            onChange={handleSelectAll}
            sx={{ p: 0 }}
          />
        );
      }
      if (column.field === "owner") {
        return (
          <Box sx={{ display: "flex", alignItems: "center", minHeight: "40px" }}>
            <Typography
              variant="body2"
              fontWeight={600}
              color={theme.customColors.text.strong}
              sx={{ lineHeight: 1.4, pr: "8px", whiteSpace: "nowrap" }}
            >
              {column.label}
            </Typography>
            <Box sx={{ width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <ColumnFilter
                column={{ ...column, filterConfig: { filterOptions: ownerFilterOptions } }}
                onFilterChange={handleFilterChange}
                filters={tableFilters["owner"] || []}
              />
            </Box>
          </Box>
        );
      }
      if (column.field === "aiEvidence") {
        return (
          <Box sx={{ display: "flex", alignItems: "center", minHeight: "40px" }}>
            <Typography
              variant="body2"
              fontWeight={600}
              color={theme.customColors.text.strong}
              sx={{ lineHeight: 1.4, pr: "8px", whiteSpace: "nowrap" }}
            >
              {column.label}
            </Typography>
            <Box sx={{ width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <ColumnFilter
                column={{
                  ...column,
                  filterConfig: { filterOptions: aiEvidenceFilterOptions },
                }}
                onFilterChange={handleFilterChange}
                filters={tableFilters["aiEvidence"] || []}
              />
            </Box>
          </Box>
        );
      }
      if (column.sortConfig?.sortable) {
        const isAsc = sortBy.includes(column.field);
        const isDesc = sortBy.includes(`-${column.field}`);
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
              gap: 0.5,
            }}
            onClick={() => handleSortChange(getNextSortBy(column.field))}
          >
            <Typography
              variant="body2"
              fontWeight={600}
              color={theme.customColors.text.strong}
            >
              {column.label}
            </Typography>
            {isAsc && <ArrowUpwardIcon sx={{ fontSize: 16 }} />}
            {isDesc && <ArrowDownwardIcon sx={{ fontSize: 16 }} />}
            {!isAsc && !isDesc && (
              <ArrowUpwardIcon
                sx={{ fontSize: 16, color: "#9CA3AF", opacity: 0.8 }}
              />
            )}
          </Box>
        );
      }
      return (
        <Typography
          variant="body2"
          fontWeight={600}
          color={theme.customColors.text.strong}
        >
          {column.label}
        </Typography>
      );
    },
    [
      sortedControls,
      selectedRowIds,
      sortBy,
      handleSelectAll,
      handleSortChange,
      getNextSortBy,
      theme,
      ownerFilterOptions,
      aiEvidenceFilterOptions,
      handleFilterChange,
      tableFilters,
    ]
  );

  const renderCell = (row, column) => {
    switch (column.field) {
      case "select":
        return (
          <Checkbox
            size="small"
            checked={selectedRowIds.has(row.controlsId)}
            onChange={() => handleSelectRow(row.controlsId)}
            sx={{ p: 0 }}
          />
        );
      case "controlsName":
        return (
          <Box sx={{ textAlign: "left", width: "100%" }}>
            <Typography
              variant="body2"
              sx={{
                textAlign: "left",
                width: "100%",
                color: theme.customColors.text.strong,
              }}
            >
              {row.controlsName || "-"}
            </Typography>
          </Box>
        );
      case "control_info":
        return (
          <Box sx={{ py: 0.5 }}>
            <Tooltip
              title={row.controlsDescription || "-"}
              arrow
              placement="top"
            >
              <Typography
                variant="body2"
                sx={{
                  fontSize: "0.875rem",
                  lineHeight: 1.5,
                  display: "-webkit-box",
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  textAlign: "left",
                  width: "100%",
                  color: theme.customColors.text.strong,
                }}
              >
                {row.controlsDescription || "-"}
              </Typography>
            </Tooltip>
          </Box>
        );
      case "aiEvidence": {
        const counts = aiEvidenceByControl.get(row?.controlsId);
        return (
          <Box sx={{ textAlign: "left", width: "100%" }}>
            <AIEvidenceChip
              pendingCount={counts?.pendingCount || 0}
              approvedCount={counts?.approvedCount || 0}
              myPendingCount={counts?.myPendingCount || 0}
              ineligible={ineligibleControlIds.has(row?.controlsId)}
            />
          </Box>
        );
      }
      case "readiness":
        return (
          <Box sx={{ textAlign: "left", width: "100%" }}>
            <ReadinessChip percentage={row?.readinessPercentage || 0} />
          </Box>
        );
      case "actions":
        return (
          <Box sx={{ display: "flex", justifyContent: "flex-end", width: "100%" }}>
            <Tooltip title="View Details" arrow>
              <IconButton
                onClick={() => handleOpenDialog(row)}
                sx={styles.actionButton}
              >
                <ArrowForwardIosIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        );
      case "owner": {
        const owner = row.controlOwnerData;
        return (
          <Box sx={{ textAlign: "left", width: "100%" }}>
            <ControlOwnerAvatar
              owner={owner}
              onClick={() => openOwnerDialog([row.controlsId], owner ?? null)}
            />
          </Box>
        );
      }
      case "domain":
        return (
          <Box sx={{ textAlign: "left", width: "100%" }}>
            <Typography
              variant="body2"
              sx={{
                textAlign: "left",
                width: "100%",
                color: theme.customColors.text.strong,
              }}
            >
              {row.domainNumbers?.join(", ") || "-"}
            </Typography>
          </Box>
        );
      default:
        return (
          <Box sx={{ textAlign: "left", width: "100%" }}>
            <Typography
              variant="body2"
              sx={{
                textAlign: "left",
                width: "100%",
                color: theme.customColors.text.strong,
              }}
            >
              {row[column.field] || "-"}
            </Typography>
          </Box>
        );
    }
  };

  return (
    <>
      {!selectedFrameworkId ? (
        <Box sx={{ textAlign: "center", py: 8 }}>
          <CircularProgress size={48} />
          <Typography variant="body1" color="textSecondary" sx={{ mt: 2 }}>
            Loading framework...
          </Typography>
        </Box>
      ) : (
        <Box sx={styles.pageContainer}>
          {/* Information Steps Card */}
          <Box sx={{ mb: 3 }}>
            <InformationStepsCard
              heading="**What should I do on this page?**"
              subtitle="This page shows all controls from your selected compliance frameworks (e.g., **SOC 2**, **ISO 27001**) and their readiness status."
              steps={[
                "View the **readiness percentage** for each control. This shows how many associated checks are passing vs. failing.",

                "Use **domain filters** to focus on specific control domains (e.g., CC1, CC2, CC3 for SOC 2). This helps you navigate through controls more efficiently.",

                "Use the **search bar** to quickly find controls by name or description, or filter by specific check names.",

                'Click **"View Details"** on any control to see all associated checks, their statuses, and detailed information about each check.',

                "Review **readiness stats** at the top to get an overview of your compliance status - see how many controls are fully ready vs. need attention.",

                "A control is **Fully Ready** when 100% of its associated checks are passing. Work on failing checks to improve control readiness.",
              ]}
              footerText="💡 Focus on controls with lower readiness percentages first to maximize your overall compliance score."
              collapsible={true}
              defaultExpanded={false}
            />
          </Box>

          {/* Header Section */}
          <Box sx={{ mb: 4 }}>
            {/* Stats Cards */}
            <Fade in={!loading} timeout={600}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                sx={{ mb: 3 }}
              >
                <Paper elevation={0} sx={styles.statsCard}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Box>
                      <Typography variant="h5" fontWeight="600" color="#1A1A1A">
                        {stats.total}
                      </Typography>
                      <Typography variant="body2" color="#6B7280">
                        Total Controls
                      </Typography>
                    </Box>
                    <AssessmentIcon sx={{ color: "#4F46E5", fontSize: 24 }} />
                  </Box>
                </Paper>

                <Paper elevation={0} sx={styles.statsCard}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Box>
                      <Typography variant="h5" fontWeight="600" color="#059669">
                        {stats.fullyReady}
                      </Typography>
                      <Typography variant="body2" color="#6B7280">
                        Fully Ready
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: "50%",
                        backgroundColor: "#10B981",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="white"
                        fontWeight="600"
                      >
                        ✓
                      </Typography>
                    </Box>
                  </Box>
                </Paper>

                <Paper elevation={0} sx={styles.statsCard}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Box>
                      <Typography variant="h5" fontWeight="600" color="#DC2626">
                        {stats.notReady}
                      </Typography>
                      <Typography variant="body2" color="#6B7280">
                        Not Ready
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: "50%",
                        backgroundColor: "#EF4444",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="white"
                        fontWeight="600"
                      >
                        !
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              </Stack>
            </Fade>
          </Box>

          <Card sx={styles.mainCard} variant="outlined">
            <CardHeader
              title={
                <Typography
                  variant="h5"
                  fontWeight="600"
                  color={theme.customColors.text.strong}
                >
                  {selectedFramework?.framework_name
                    ? `${selectedFramework.framework_name} Controls Overview`
                    : "Controls Overview"}
                </Typography>
              }
              sx={styles.cardHeader}
              action={
                <Box sx={{ display: "flex", alignItems: "center", maxHeight: 35, justifyContent: "center" }}>
                  {selectedRowIds.size > 0 ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <Typography
                        variant="body2"
                        sx={{ color: theme.customColors.text.secondary, fontWeight: 500 }}
                      >
                        {selectedRowIds.size} control{selectedRowIds.size > 1 ? "s" : ""} selected
                      </Typography>
                      <Button
                        variant="contained"
                        startIcon={<PersonIcon sx={{ fontSize: 16 }} />}
                        size="small"
                        onClick={() => openOwnerDialog(Array.from(selectedRowIds), null)}
                        sx={{
                          bgcolor: theme.customColors.button.primary,
                          color: theme.customColors.button.text,
                          fontWeight: 500,
                          maxHeight: 35,
                          borderRadius: "8px",
                          textTransform: "none",
                          fontSize: "14px",
                          px: 2,
                          py: 1,
                          "&:hover": {
                            bgcolor: theme.palette._blue?.dark ?? theme.customColors.button.hover,
                          },
                          "&:disabled": {
                            bgcolor: theme.customColors.background.light,
                            color: theme.customColors.text.disabled,
                          },
                        }}
                      >
                        Change Owner
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setSelectedRowIds(new Set())}
                        sx={{
                          fontWeight: 500,
                          borderRadius: "8px",
                          textTransform: "none",
                          maxHeight: 35,
                          fontSize: "14px",
                          px: 2,
                          py: 1,
                          borderColor: theme.customColors.border.main,
                          color: theme.customColors.text.strong,
                          "&:hover": {
                            borderColor: theme.customColors.border.strong,
                            backgroundColor: theme.customColors.background.hover,
                          },
                        }}
                      >
                        Clear
                      </Button>
                    </Box>
                  ) : (
                    <SearchBar
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search controls..."
                      count={filteredControls.length}
                      countLabel="controls found"
                    />
                  )}
                </Box>
              }
            />

            <CardContent sx={{ p: 0 }}>
              {domains.length > 0 && (
                <Box sx={styles.domainFilterContainer}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 3,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <FilterListIcon sx={{ color: "#6B7280", fontSize: 20 }} />
                      <Typography
                        variant="body1"
                        color="#374151"
                        fontWeight="600"
                      >
                        Filter by Domain
                      </Typography>
                    </Box>
                    {(searchQuery || selectedDomain) && (
                      <Tooltip title="Clear all filters" arrow>
                        <IconButton
                          size="small"
                          onClick={clearFilters}
                          sx={styles.clearFilterButton}
                        >
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                  <Stack direction="row" sx={{ flexWrap: "wrap", gap: 1.5 }}>
                    {domains.map((domain) => {
                      const controlCount = getDomainControlCount(domain);
                      return (
                        <Badge
                          key={domain}
                          color="primary"
                          sx={
                            selectedDomain === domain
                              ? styles.badgeSelected
                              : styles.badge
                          }
                        >
                          <Chip
                            label={domain}
                            onClick={() =>
                              setSelectedDomain(
                                selectedDomain === domain ? null : domain
                              )
                            }
                            variant={
                              selectedDomain === domain ? "filled" : "outlined"
                            }
                            sx={
                              selectedDomain === domain
                                ? styles.domainChipSelected
                                : styles.domainChip
                            }
                          />
                        </Badge>
                      );
                    })}
                  </Stack>
                </Box>
              )}

              <Divider sx={{ borderColor: "#E8EAED" }} />

              <Box>
                <ReusableTable
                  loading={shouldShowTableLoading}
                  columns={tableColumns}
                  data={sortedControls}
                  renderCell={renderCell}
                  renderHeaderCell={renderHeaderCell}
                  height="auto"
                  headerRightPadding="17px"
                  showPagination={true}
                  rowsPerPageOptions={[50, 100]}
                  sortBy={sortBy}
                  onSortChange={handleSortChange}
                  emptyState={
                    showNoOwnedEvidence ? (
                      <AIEvidenceAccessEmptyState
                        onClearFilter={() =>
                          handleFilterChange("aiEvidence", [])
                        }
                      />
                    ) : null
                  }
                />

              </Box>
            </CardContent>
          </Card>

          {selectedControl && (
            <ControlDetailDialog
              open={isDialogOpen}
              onClose={() => setIsDialogOpen(false)}
              control={selectedControl}
              refetchData={refetchFrameworkDetails}
              onOwnerChanged={handleOwnerChangeSuccess}
            />
          )}

          <UserSelectionDialog
            open={ownerDialog.open}
            onClose={closeOwnerDialog}
            title="Change Control Owner"
            description={
              ownerDialog.controlsIds.length === 1
                ? "1 control will be updated."
                : `${ownerDialog.controlsIds.length} controls will be updated.`
            }
            label="Control owner"
            currentUser={ownerDialog.currentOwner}
            onConfirm={handleOwnerConfirm}
            saving={ownerSaving}
            adminsOnly
            showUnassignOption
          />
        </Box>
      )}
    </>
  );
};

export default ControlsHome;
